import type { GameId, PricingConfiguration } from "./model/models";
import { bundledGameMetadata } from "./gameRegistry";
import {
  CardPriceCurrenciesEnum,
  CardPriceMarketEnum,
} from "../cards/model/card-price";
import {
  CardLanguageEnum,
  type CardPrinting,
  type CardPrintingMarketSku,
  type FinishEnum,
} from "../cards/model/card";

export enum PricingVariant {
  CardmarketEur = "cardmarket-eur",
  TcgplayerUsd = "tcgplayer-usd",
}

export interface PricingSource {
  pricing: PricingConfiguration;
  variant: PricingVariant;
  name: string;
  description: string;
  suggestedRegions: readonly string[];
  productUrl: (
    game: GameId,
    productId: string,
    priceVariant?: string
  ) => string | undefined;
  primaryChartSeriesLabel: string;
}

const PURCHASE_QUERY =
  "utm_source=lootstack&utm_medium=website&utm_campaign=card_prices";

function tcgplayerProductUrl(
  game: GameId,
  productId: string,
  priceVariant?: string
): string | undefined {
  const registration = bundledGameMetadata(game);
  if (registration && !registration.pricing[PricingVariant.TcgplayerUsd]) {
    return undefined;
  }
  const query = new URLSearchParams();
  if (priceVariant) query.set("Printing", priceVariant);
  query.set("utm_source", "lootstack");
  query.set("utm_medium", "website");
  query.set("utm_campaign", "card_prices");
  return `https://www.tcgplayer.com/product/${encodeURIComponent(productId)}?${query.toString()}`;
}

function cardmarketProductUrl(
  game: GameId,
  productId: string
): string | undefined {
  const gamePath =
    bundledGameMetadata(game)?.pricing[PricingVariant.CardmarketEur]
      ?.marketplaceProductPath;
  if (!gamePath) return undefined;
  return `https://www.cardmarket.com/en/${gamePath}/Products?idProduct=${encodeURIComponent(productId)}&${PURCHASE_QUERY}`;
}

const PRICING_SOURCE_BY_MARKET: Record<CardPriceMarketEnum, PricingSource> = {
  [CardPriceMarketEnum.TCGPlayer]: {
    pricing: {
      market: CardPriceMarketEnum.TCGPlayer,
      currency: CardPriceCurrenciesEnum.USD,
    },
    variant: PricingVariant.TcgplayerUsd,
    name: "TCGplayer",
    description: "US-dollar card prices",
    suggestedRegions: ["US", "CA"],
    productUrl: tcgplayerProductUrl,
    primaryChartSeriesLabel: "Market",
  },
  [CardPriceMarketEnum.Cardmarket]: {
    pricing: {
      market: CardPriceMarketEnum.Cardmarket,
      currency: CardPriceCurrenciesEnum.EUR,
    },
    variant: PricingVariant.CardmarketEur,
    name: "Cardmarket",
    description: "Euro card prices",
    suggestedRegions: [],
    productUrl: cardmarketProductUrl,
    primaryChartSeriesLabel: "Trend",
  },
};

export const PRICING_SOURCES: readonly PricingSource[] = Object.values(
  PRICING_SOURCE_BY_MARKET
);

export function pricingSourceForMarket(
  market: CardPriceMarketEnum
): PricingSource | undefined {
  return PRICING_SOURCE_BY_MARKET[market];
}

function marketSkuLanguageRank(language?: CardLanguageEnum): number {
  if (!language) return 0;
  return language === CardLanguageEnum.English ? 1 : 2;
}

/**
 * Resolves one exact provider product for the selected physical finish.
 * Language-neutral bindings win, followed by English. If the best language
 * rank still points at multiple products, no marketplace link is rendered.
 */
export function preferredMarketSku(
  printing: CardPrinting | undefined,
  market: CardPriceMarketEnum,
  finish: FinishEnum
): CardPrintingMarketSku | undefined {
  const candidates = (printing?.marketSkus ?? []).filter(
    (sku) =>
      sku.provider === market &&
      sku.finish === finish &&
      sku.productId.trim() !== ""
  );
  if (candidates.length === 0) return undefined;

  const bestRank = Math.min(
    ...candidates.map((sku) => marketSkuLanguageRank(sku.language))
  );
  const byProduct = new Map<string, CardPrintingMarketSku>();
  for (const sku of candidates) {
    if (marketSkuLanguageRank(sku.language) !== bestRank) continue;
    byProduct.set(
      `${sku.productId.trim()}\u0000${sku.priceVariant?.trim().toLowerCase() ?? ""}`,
      sku
    );
  }
  return byProduct.size === 1 ? byProduct.values().next().value : undefined;
}

export function marketplacePurchaseUrl(
  game: GameId,
  printing: CardPrinting | undefined,
  market: CardPriceMarketEnum,
  finish: FinishEnum
): string | undefined {
  const source = pricingSourceForMarket(market);
  const sku = preferredMarketSku(printing, market, finish);
  return source && sku
    ? source.productUrl(game, sku.productId, sku.priceVariant)
    : undefined;
}

const DEFAULT_PRICING_SOURCE =
  PRICING_SOURCE_BY_MARKET[CardPriceMarketEnum.Cardmarket];
export const DEFAULT_PRICING = DEFAULT_PRICING_SOURCE.pricing;

const TCGPLAYER_SOURCE =
  PRICING_SOURCE_BY_MARKET[CardPriceMarketEnum.TCGPlayer];

export const PRICING_VARIANT_COOKIE = "lootstack-pricing";
export const PRICING_VARIANT_QUERY_PARAM = "__lootstack_pricing";
export const PRICING_VARIANT_SESSION_CLAIM = "pricing_variant";

const PRICING_COOKIE_VERSION = "v1";

export interface PricingSessionContext {
  userId: string;
  variant?: PricingVariant;
}

export function isPricingCatalogPath(pathname: string): boolean {
  const segments = pathname.split("?")[0].split("/").filter(Boolean);
  if (segments[0] === "decks") {
    return segments.length === 3 || segments.length === 4;
  }
  if (segments[0] !== "sets") return false;

  const isSetsBrowser = segments.length === 2;
  const isSetPage = segments.length === 3;
  const isCardPage = segments.length === 5 && segments[3] !== "sealed";
  return isSetsBrowser || isSetPage || isCardPage;
}

export function pricingForVariant(
  variant: PricingVariant
): PricingConfiguration {
  return (
    PRICING_SOURCES.find((source) => source.variant === variant)?.pricing ??
    DEFAULT_PRICING
  );
}

export function pricingVariantForPricing(
  pricing: PricingConfiguration
): PricingVariant {
  const source = pricingSourceForMarket(pricing.market);
  return source?.pricing.currency === pricing.currency
    ? source.variant
    : DEFAULT_PRICING_SOURCE.variant;
}

export function pricingVariantForRegion(
  region?: string | null
): PricingVariant {
  const normalizedRegion = region?.toUpperCase();
  return normalizedRegion &&
    TCGPLAYER_SOURCE.suggestedRegions.includes(normalizedRegion)
    ? PricingVariant.TcgplayerUsd
    : PricingVariant.CardmarketEur;
}

export function parsePricingVariant(
  value?: string | null
): PricingVariant | undefined {
  return Object.values(PricingVariant).includes(value as PricingVariant)
    ? (value as PricingVariant)
    : undefined;
}

export function pricingVariantFromCookieHeader(
  cookieHeader?: string | null,
  signedInUserId?: string
): PricingVariant | undefined {
  const values = cookieValues(cookieHeader, PRICING_VARIANT_COOKIE);
  if (signedInUserId) {
    for (const value of values) {
      const [version, encodedUserId, encodedVariant] = value.split(":");
      if (
        version !== PRICING_COOKIE_VERSION ||
        !encodedUserId ||
        !encodedVariant
      ) {
        continue;
      }
      try {
        if (decodeURIComponent(encodedUserId) !== signedInUserId) continue;
      } catch {
        continue;
      }
      const variant = parsePricingVariant(encodedVariant);
      if (variant) return variant;
    }
    return undefined;
  }

  for (const value of values) {
    // Account-bound cookies are ignored after logout. Legacy plain cookies
    // remain valid for anonymous visitors during the rollout.
    if (value.startsWith(`${PRICING_COOKIE_VERSION}:`)) continue;
    const variant = parsePricingVariant(value);
    if (variant) return variant;
  }
  return undefined;
}

export function pricingCookieValue(
  variant: PricingVariant,
  userId?: string
): string {
  return userId
    ? `${PRICING_COOKIE_VERSION}:${encodeURIComponent(userId)}:${variant}`
    : variant;
}

/**
 * Reads only presentation hints from the Clerk session token. This deliberately
 * does not verify the JWT because the result controls public pricing rendering
 * and cache selection, never authentication or authorization.
 */
export function pricingSessionFromRequestHeaders(
  cookieHeader?: string | null,
  authorizationHeader?: string | null
): PricingSessionContext | undefined {
  const bearerToken = authorizationHeader?.match(/^Bearer\s+(.+)$/i)?.[1];
  const token = bearerToken || cookieValue(cookieHeader, "__session");
  if (!token) return undefined;

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.sub !== "string" || !payload.sub) {
    return undefined;
  }

  const variant = parsePricingVariant(
    stringValue(payload[PRICING_VARIANT_SESSION_CLAIM])
  );

  return { userId: payload.sub, variant };
}

export function pricingForLocale(
  locale = Intl.DateTimeFormat().resolvedOptions().locale
): PricingConfiguration {
  const region = locale.match(/[-_]([A-Z]{2})(?:-|$)/i)?.[1]?.toUpperCase();
  return pricingForVariant(pricingVariantForRegion(region));
}

export function pricingCacheKey(
  pricing: PricingConfiguration
): PricingConfiguration | undefined {
  return pricing.market === DEFAULT_PRICING.market &&
    pricing.currency === DEFAULT_PRICING.currency
    ? undefined
    : pricing;
}

export function withPricingCacheKey(
  baseKey: readonly unknown[],
  pricingKey?: unknown
) {
  return pricingKey ? [...baseKey, pricingKey] : baseKey;
}

export function appendPricingParams(
  params: URLSearchParams,
  pricing?: PricingConfiguration
) {
  if (!pricing) return;
  params.set("market", pricing.market);
  params.set("currency", pricing.currency);
}

function cookieValue(
  cookieHeader: string | null | undefined,
  name: string
): string | undefined {
  return cookieValues(cookieHeader, name)[0];
}

function cookieValues(
  cookieHeader: string | null | undefined,
  name: string
): string[] {
  if (!cookieHeader) return [];

  const values: string[] = [];
  for (const part of cookieHeader.split(";")) {
    const [cookieName, ...valueParts] = part.trim().split("=");
    if (cookieName === name) values.push(valueParts.join("="));
  }
  return values;
}

function decodeJwtPayload(token: string): Record<string, unknown> | undefined {
  const encoded = token.split(".")[1];
  if (!encoded) return undefined;

  try {
    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return objectValue(JSON.parse(atob(padded)));
  } catch {
    return undefined;
  }
}

function objectValue(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}
